// ============================================================
// Wegweisung und Gerüst.
//
// Drei Ebenen, und jede hat genau eine Aufgabe (§5.8.1):
//   Wurzel       — Meldungen, der EINE Aufruf von GET /me, Auffangkorb
//   Geschuetzt   — Kopfleiste und die Frage, ob jemand hier sein darf
//   Bildschirm   — der Inhalt
//
// Der Auffangkorb liegt ÜBER dem Router-Outlet, nicht darin: stürzt ein
// Bildschirm ab, soll die Kopfleiste stehen bleiben, damit man wegnavigieren
// kann statt neu zu laden.
//
// EINE Ausnahme bei der Kopfleiste: auf /plan/:id zeichnet sie der EDITOR
// selbst (§5.8.1, §5.7.3). Sie ist dieselbe Komponente in ihrer
// Werkzeugfassung — nur muss der Editor ihr sagen können, welche Bauteile
// „Stände" und „Exportieren" öffnen, und die hält er.
// ============================================================
import { Suspense, lazy, useEffect, useRef } from "react";
import { createBrowserRouter, Navigate, Outlet, RouterProvider, useLocation } from "react-router-dom";
import { Spinner, Toaster } from "./components/ui";
import { ScreenBoundary } from "./components/app/ScreenBoundary";
import { TopNav } from "./components/app/TopNav";
import { Anmelden } from "./screens/Anmelden";
import { useAuthStore } from "./store/useAuthStore";
import { TX } from "./lib/texte";

// Der Editor zieht den Zeichenkern, die Zeigerbehandlung und (dynamisch)
// jspdf hinter sich her; die Verwaltung sehen zwei Menschen. Beides gehört
// nicht in den Eingang. Die Anmeldung bleibt EAGER: sie ist für jeden
// Abgemeldeten das erste Bild, und ein Nachladeschritt davor wäre ein
// leerer Bildschirm ohne Grund.
const Planliste = lazy(() => import("./screens/Planliste").then((m) => ({ default: m.Planliste })));
const Editor = lazy(() => import("./screens/Editor").then((m) => ({ default: m.Editor })));
const ImportPruefung = lazy(() => import("./screens/ImportPruefung").then((m) => ({ default: m.ImportPruefung })));
const Papierkorb = lazy(() => import("./screens/Papierkorb").then((m) => ({ default: m.Papierkorb })));
const Vorlagen = lazy(() => import("./screens/Vorlagen").then((m) => ({ default: m.Vorlagen })));
const ClaudeZugang = lazy(() => import("./screens/ClaudeZugang").then((m) => ({ default: m.ClaudeZugang })));
const Verbinden = lazy(() => import("./screens/Verbinden").then((m) => ({ default: m.Verbinden })));
const Admin = lazy(() => import("./screens/Admin").then((m) => ({ default: m.Admin })));
const NichtGefunden = lazy(() => import("./screens/NichtGefunden").then((m) => ({ default: m.NichtGefunden })));

function Wartezeichen() {
  return (
    <div style={{ height: "60vh", display: "grid", placeItems: "center" }} role="status" aria-label={TX.laedt}>
      <Spinner size={22} />
    </div>
  );
}

function Wurzel() {
  const laden = useAuthStore((s) => s.laden);
  // Genau EIN Aufruf, auch im StrictMode (der Effekte zweimal fährt). Zwei
  // GET /me wären harmlos, aber sie sind auch ohne Nutzen.
  const geladen = useRef(false);

  useEffect(() => {
    if (geladen.current) return;
    geladen.current = true;
    void laden();
  }, [laden]);

  return (
    <>
      <Outlet />
      {/* GENAU EINMAL in der ganzen Anwendung. */}
      <Toaster />
    </>
  );
}

function Geschuetzt() {
  const status = useAuthStore((s) => s.auth.status);
  const ort = useLocation();

  // "unbekannt" heißt: GET /me läuft noch. Ein Wartezeichen, KEINE Umleitung
  // — sonst sieht ein angemeldeter Mensch bei jedem Neuladen für den
  // Bruchteil einer Sekunde die Anmeldung.
  if (status === "unbekannt") return <Wartezeichen />;
  // Nach einem 401 aus irgendeinem Aufruf setzt `wegweiser` den Status auf
  // "anonym", und dieser Zweig schickt zur Anmeldung — OHNE Toast: ein
  // abgelaufenes Cookie ist kein Fehler (§5.8.1).
  // Der Weg geht mit (T48): die Freigabeseite für Claude muss nach der
  // Anmeldung wieder erscheinen, sonst endet die Verbindung auf der Planliste.
  if (status !== "angemeldet") return <Navigate to="/anmelden" replace state={{ zurueck: ort.pathname }} />;

  const imEditor = ort.pathname.startsWith("/plan/");

  return (
    <div className="app-schale">
      {!imEditor && <TopNav />}
      {/* Der Schlüssel setzt den Auffangkorb bei jedem Wegwechsel zurück.
          Ohne ihn bliebe ein einmal abgestürzter Bildschirm auch dann noch
          als Fehlerseite stehen, wenn man längst woanders hingeklickt hat. */}
      <ScreenBoundary key={ort.pathname}>
        <Suspense fallback={<Wartezeichen />}>
          <Outlet />
        </Suspense>
      </ScreenBoundary>
    </div>
  );
}

/** `/admin` verlangt zusätzlich die Rolle (§5.7.7). Kein Toast, keine Erklärung. */
function NurAdmin() {
  const rolle = useAuthStore((s) => s.auth.user?.rolle);
  const status = useAuthStore((s) => s.auth.status);
  // `user` kann nach `angemeldet()` noch null sein, bis `laden()` zurück ist.
  if (status === "angemeldet" && rolle === undefined) return <Wartezeichen />;
  if (rolle !== "admin") return <Navigate to="/" replace />;
  return <Outlet />;
}

const router = createBrowserRouter([
  {
    element: <Wurzel />,
    children: [
      {
        path: "/anmelden",
        element: (
          <ScreenBoundary>
            <Anmelden />
          </ScreenBoundary>
        ),
      },
      {
        element: <Geschuetzt />,
        children: [
          { path: "/", element: <Planliste /> },
          { path: "/plan/:id", element: <Editor /> },
          { path: "/import/:id", element: <ImportPruefung /> },
          { path: "/papierkorb", element: <Papierkorb /> },
          { path: "/vorlagen", element: <Vorlagen /> },
          { path: "/claude", element: <ClaudeZugang /> },
          { path: "/verbinden/:id", element: <Verbinden /> },
          { element: <NurAdmin />, children: [{ path: "/admin", element: <Admin /> }] },
          { path: "*", element: <NichtGefunden /> },
        ],
      },
    ],
  },
]);

export function App() {
  return <RouterProvider router={router} />;
}
